import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { SupportRequestCategory } from "@fmc/contracts";
import { api } from "../api";

const categories: Array<{ value: SupportRequestCategory; label: string }> = [
  { value: "question", label: "Question" },
  { value: "technical_issue", label: "Technical issue" },
  { value: "access_request", label: "Access request" },
  { value: "feature_request", label: "Feature request" },
];

const fieldClassName =
  "w-full rounded-xs border border-default bg-surface-white px-3 text-body text-primary outline-none transition-colors placeholder:text-tertiary focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 disabled:bg-surface-light";

export function HelpDialog({ onClose }: { onClose: () => void }) {
  const titleId = useId();
  const descriptionId = useId();
  const categoryRef = useRef<HTMLSelectElement>(null);
  const [category, setCategory] = useState<SupportRequestCategory>("question");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    categoryRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, saving]);

  const valid = subject.trim().length >= 3 && message.trim().length >= 10;
  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      await api("/api/support-requests", {
        method: "POST",
        body: JSON.stringify({
          category,
          subject,
          message,
          page: `${window.location.pathname}${window.location.search}`,
        }),
      });
      setSent(true);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Your request could not be sent.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center px-5 py-8">
      <button
        type="button"
        aria-label="Close Help"
        className="absolute inset-0 bg-[var(--overlay-backdrop)]"
        onClick={() => {
          if (!saving) onClose();
        }}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="relative w-full max-w-[560px] overflow-hidden rounded-xl border border-default bg-surface-white shadow-[var(--shadow-md)]"
      >
        <header className="flex items-start justify-between gap-4 border-b border-default px-6 py-5">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-surface-info text-brand-dark-teal">
              <i className="fa-solid fa-circle-question text-[17px]" aria-hidden="true" />
            </span>
            <div>
              <h2 id={titleId} className="text-title-md font-bold text-primary">
                {sent ? "Request sent" : "How can we help?"}
              </h2>
              <p id={descriptionId} className="mt-1 text-caption text-secondary">
                {sent
                  ? "The FMC Leads Manager team has received your request."
                  : "Send a question or support request to the FMC Leads Manager team."}
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close Help"
            disabled={saving}
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xs text-tertiary transition-colors hover:bg-surface-light hover:text-primary disabled:opacity-50"
          >
            <i className="fa-solid fa-xmark text-[17px]" aria-hidden="true" />
          </button>
        </header>

        {sent ? (
          <div className="px-6 py-6">
            <div className="flex items-start gap-3 rounded-md border border-status-green/30 bg-[rgba(86,205,174,0.1)] px-4 py-3.5">
              <i className="fa-solid fa-circle-check mt-0.5 text-status-green" aria-hidden="true" />
              <p className="text-body text-primary">
                We will reply to the email address connected to your Leads Manager account.
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                autoFocus
                onClick={onClose}
                className="h-10 rounded-xs bg-brand-red px-5 text-body font-semibold text-white transition-colors hover:bg-brand-red-hover"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <div className="flex flex-col gap-4 px-6 py-5">
              <div>
                <label htmlFor="help-category" className="text-caption font-semibold text-primary">
                  What can we help with?
                </label>
                <select
                  ref={categoryRef}
                  id="help-category"
                  value={category}
                  disabled={saving}
                  onChange={(event) => setCategory(event.target.value as SupportRequestCategory)}
                  className={`${fieldClassName} mt-1.5 h-10`}
                >
                  {categories.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="help-subject" className="text-caption font-semibold text-primary">
                  Subject
                </label>
                <input
                  id="help-subject"
                  value={subject}
                  disabled={saving}
                  maxLength={160}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="A short summary of your request"
                  className={`${fieldClassName} mt-1.5 h-10`}
                />
              </div>

              <div>
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor="help-message" className="text-caption font-semibold text-primary">
                    Message
                  </label>
                  <span className="text-micro text-tertiary">{message.length}/5,000</span>
                </div>
                <textarea
                  id="help-message"
                  value={message}
                  disabled={saving}
                  maxLength={5_000}
                  rows={7}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Tell us what happened, what you expected, and anything else that may help."
                  className={`${fieldClassName} mt-1.5 resize-y py-2.5`}
                />
              </div>

              <div className="flex items-start gap-2 rounded-md bg-surface-muted px-3.5 py-3 text-caption text-secondary">
                <i className="fa-solid fa-shield-halved mt-0.5 text-tertiary" aria-hidden="true" />
                <span>Please do not include patient information or sensitive lead data.</span>
              </div>

              {error && (
                <p role="alert" className="text-caption text-status-red">
                  {error}
                </p>
              )}
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-default px-6 py-4">
              <button
                type="button"
                disabled={saving}
                onClick={onClose}
                className="h-10 rounded-xs border border-default bg-surface-white px-4 text-body font-medium text-secondary transition-colors hover:bg-surface-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!valid || saving}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xs bg-brand-red px-4 text-body font-semibold text-white transition-colors hover:bg-brand-red-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                <i className="fa-solid fa-paper-plane text-[12px]" aria-hidden="true" />
                {saving ? "Sending…" : "Send request"}
              </button>
            </footer>
          </form>
        )}
      </section>
    </div>,
    document.body,
  );
}
